import { Button, Icon, Logo } from '@platform/components';
import FlexibleLink from '../util/FlexibleLink';
import { AsSeenIn, TrustedBy } from './Logos';
import { HeaderFooterLayout } from '../app';
import { ROUTES } from '../routes/consts';
import SocialImpactNews from '../util/SocialImpactNews';
import { ContentfulRootData } from '../contentful/util/types';
import { getBlogArticles, getBlogUrl } from '../contentful/blog/util';
import { TypeBlogArticle } from '@platform/contentful';

const ctaRowItems: {
  image: string;
  title: string;
  description: string;
  ctaText: string;
  ctaUrl: string;
  isLegacy: boolean;
}[] = [
  {
    image:
      '//images.ctfassets.net/e6h4r2c2hknq/4xDXB0SXn5I7S3CGBcH0qD/045558549dcce56b6c44bc605d5b5299/Entrepreneurs-at-Anza-Tanzania-min.jpg?fit=fill&w=125&h=125',
    title: 'Social Enterprises',
    description: 'Grow your revenue, keep your equity',
    ctaText: 'Learn more and apply',
    ctaUrl: '/platform' + ROUTES.SOCIAL_ENTERPRISES,
    isLegacy: false,
  },
  {
    image:
      '//images.ctfassets.net/e6h4r2c2hknq/5IXUTf8ZyYCTpiQ7pgVU84/c7849f20ce839002f279753b249420d9/Heather_photo_edited.png?fit=fill&w=125&h=125',
    title: 'Professionals',
    description: 'Join a social impact + career growth program',
    ctaText: 'Learn more and register',
    ctaUrl: '/platform' + ROUTES.PROFESSIONALS,
    isLegacy: false,
  },
  {
    image:
      '//images.ctfassets.net/e6h4r2c2hknq/4R7R1UyFATPTgKyUK9L3xg/79c48847e61592b1aa1e4068ae78f42e/microsoft-employees-corporate-volunteering-programs.jpg?fit=fill&w=125&h=125',
    title: 'Corporates',
    description: 'Activate employees to lead sustainable business transformation',
    ctaText: 'Explore our CSR platform',
    ctaUrl: '/skills-based-volunteering-platform',
    isLegacy: true,
  },
  {
    image:
      '//images.ctfassets.net/e6h4r2c2hknq/5yu6Jaygv4j5j3yX9bEjGe/5fc4f1ecc8c722d873e908e35d5a3b29/mwi-global-fellows-lisbon-2.jpg?fit=fill&w=125&h=125',
    title: 'Impact Partners',
    description: 'Accelerate your investees, grow your pipeline',
    ctaText: 'See partnership benefits',
    ctaUrl: '/impact-partners',
    isLegacy: true,
  },
] as const;

const testimonialRowItems: {
  image: string;
  hightlightText: string;
  titleText: string;
  quoteText: string;
}[] = [
  {
    image:
      '//images.ctfassets.net/e6h4r2c2hknq/deT6y9JedjMP5Lrs7RqMW/c1e03f35e2d962faa10a69e93c359a18/Sarah-Gonzalez.jpg?f=face&fit=thumb&w=150&h=150',
    hightlightText: 'Professional Highlight',
    titleText: 'Launching a Values-Aligned Portfolio Career',
    quoteText:
      "“Experteering my skills in a new context helped me realize that I do have what it takes to make a meaningful social impact, and I'm proud to have contributed to a social enterprise building a better world. ”",
  },
  {
    image:
      '//images.ctfassets.net/e6h4r2c2hknq/4Oc3BwWZ1Ro5gm7agBLwI2/bdc30446936f0c6dafbb6bf046272d13/minhaj.jpg?f=face&fit=thumb&w=150&h=150',
    hightlightText: 'Social Enterprise Highlight',
    titleText: 'Finding the Right Connections to Accelerate Impact',
    quoteText:
      '“I feel like I hit the jackpot with the connections and advice I was able to find through MovingWorlds and its network.”',
  },
  {
    image:
      '//images.ctfassets.net/e6h4r2c2hknq/6cYXqK7Flnr6vlQMpAL0MG/4720faf58f102833b457729f1d04977e/alexandra_van_der_ploeg_sap.jpg?f=face&fit=thumb&w=150&h=150',
    hightlightText: 'Corporate Highlight',
    titleText: 'Scaling Business as a Force for Good',
    quoteText:
      '“The MovingWorlds platform makes it fast, simple, and cost-effective to engage our employees at scale from across the globe.”',
  },
  {
    image:
      '//images.ctfassets.net/e6h4r2c2hknq/2D2RLV5VEsOall6GzZhzsr/982609d34178f74ac3c5047d9b7f93ba/DeirdreWhite_edit_web.jpg?f=face&fit=thumb&w=150&h=150',
    hightlightText: 'Accelerator Highlight',
    titleText: 'Joining Forces for a More Sustainable World',
    quoteText:
      '“Together with SAP and MovingWorlds, we can support more social enterprises and entrepreneurs than we ever could apart.”',
  },
];

const Divider = () => {
  return <hr className='w-1/3 h-[2px] my-4 bg-accent-500 border-none' />;
};

const CircleImage = ({ imageUrl, imageAlt }: { imageUrl: string; imageAlt: string }) => {
  return (
    <img
      className='my-auto md:my-0 w-28 h-28 md:w-32 md:h-32 rounded-full'
      src={imageUrl}
      alt={imageAlt}
    />
  );
};

const SeeOurNetworkButton = () => {
  return (
    <Button variant='primary' size='lg' href='https://movingworlds.org/network-report'>
      See our network
    </Button>
  );
};

const BlogCard = ({
  title,
  subtitle,
  imageUrl,
  linkUrl,
}: {
  title: string;
  subtitle: string;
  imageUrl: string;
  linkUrl: string;
}) => {
  return (
    <a
      className='bg-white rounded-md shadow-md md:transition-all md:hover:-translate-y-2 md:hover:shadow-lg'
      href={linkUrl}
      target='_blank'
    >
      <img
        className='w-full h-40 md:h-64 object-cover rounded-md rounded-b-none'
        src={imageUrl}
        alt={title}
      />
      <div className='flex flex-col gap-2 p-4'>
        <h3 className='text-base md:text-xl font-semibold'>{title}</h3>
        <Divider />
        <p className='text-sm text-gray-600'>{subtitle}</p>
      </div>
    </a>
  );
};

export type BlogCardData = {
  title: string;
  subtitle: string;
  imageUrl: string;
  linkUrl: string;
};

export const blogArticleToHomepageSummary = (
  item: TypeBlogArticle<'WITHOUT_UNRESOLVABLE_LINKS', 'en-US'>,
): BlogCardData => {
  return {
    title: item.fields.title,
    subtitle: item.fields.metaDescription,
    imageUrl:
      item.fields.bannerImage?.fields.file?.url ??
      'https://optimole.movingworlds.org/w:414/h:310/q:mauto/f:best/ig:avif/https://blog.movingworlds.org/wp-content/uploads/2017/08/PowerPoiunt-Background.png',
    linkUrl: getBlogUrl(item.fields.slug),
  };
};

const HomePage = ({ contentfulData }: { contentfulData: ContentfulRootData | null }) => {
  /* Load the blog article cards. In local dev the whole Contentful payload will have been passed as a prop (if available).
   * During a static build, fetch the static data that's been baked into the page in the global contentful object */
  let blogCards: BlogCardData[] = [];
  if (contentfulData) {
    blogCards = getBlogArticles(contentfulData.items).slice(0, 3).map(blogArticleToHomepageSummary);
  } else if (typeof window !== 'undefined' && 'CONTENTFUL_PAGE_DATA' in window) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    blogCards = (window as any)['CONTENTFUL_PAGE_DATA'] as BlogCardData[];
  }

  return (
    <HeaderFooterLayout>
      <div className='md:-mt-12 flex flex-col'>
        {/* Mission statement banner & image */}
        <div className='bg-gray-50 flex flex-col md:flex-row'>
          <div className='self-center md:w-full md:max-w-[41.666667%] py-10 px-4 md:p-10'>
            <h1 className='text-4xl md:text-5xl font-bold text-gray-500'>
              Global Challenges Require Global Solutions
            </h1>
            <Divider />
            <h2 className='font-header text-base md:text-xl font-medium text-gray-500'>
              MovingWorlds operates a global platform that convenes partners from all sectors to
              build a more equitable, just, and sustainable economy.
            </h2>
          </div>
          <div
            style={{
              backgroundImage:
                "url('https://optimole.movingworlds.org/h:960/w:1920/rt:fill/q:85/https://images.ctfassets.net/e6h4r2c2hknq/3m03MDnfN7beWPY7WgZfAG/bea8d782181930e5ef17794fa6ee4809/Fellows_working_together.jpg?fit=fill&w=1920&h=960'",
            }}
            className='w-full h-[30vh] md:h-full min-h-[300px] md:min-h-[60vh] max-h-[600px] bg-no-repeat bg-center bg-cover'
          />
        </div>
        {/* CTA Row */}
        <div className='mx-4 my-9 flex flex-col md:grid md:grid-cols-4 md:max-w-7xl md:mx-auto gap-6 text-left md:text-center md:justify-center'>
          {ctaRowItems.map((item, index) => (
            <div key={index} className='flex md:flex-col md:items-center gap-4 md:max-w-7xl'>
              <CircleImage imageUrl={item.image} imageAlt={item.title} />
              <div className='md:h-full md:flex md:flex-col md:items-center'>
                <h3 className='text-xl md:text-2xl font-semibold text-gray-500'>{item.title}</h3>
                <h4 className='md:mb-4 text-base md:text-xl text-gray-500'>{item.description}</h4>
                {/* An always-present bottom border is used but kept transparent to make the height consistent when mousing over shows the blue underline.
                 * We use a border instead of underline so the border displays under the right arrow */}
                <span className='mt-auto md:inline-block text-brand-500 border-b-2 border-b-transparent hover:border-b-2 hover:border-b-brand-500 font-semibold [&>a]:flex [&>a]:items-center'>
                  <FlexibleLink href={item.ctaUrl} type={item.isLegacy ? 'legacy' : 'internal'}>
                    {item.ctaText}
                    <span className='md:ml-auto'>
                      <Icon type='arrow-right' size='20' />
                    </span>
                  </FlexibleLink>
                </span>
              </div>
            </div>
          ))}
        </div>
        {/* Testimonials */}
        <div className='z-0 relative px-4 md:px-10 md:py-16 bg-gray-50'>
          {/* Floating backgroudn MovingWorlds logo */}
          <div className='hidden md:block absolute left-9 top-9 -z-10'>
            <Logo type='logomark' variant='gray' size='410' />
          </div>
          <div className='flex flex-col md:flex-row md:max-w-7xl md:mx-auto'>
            <div className='md:pr-4 flex flex-col items-center md:items-start text-center md:text-left md:gap-6'>
              <h1 className='text-4xl md:text-5xl font-bold text-gray-500'>
                Discover the MovingWorlds ecosystem
              </h1>
              <Divider />
              <h2 className='text-base md:text-xl font-medium text-gray-500'>
                Empowering social enterprises, and the people working with — and within — them.
              </h2>
              <div className='hidden md:block'>
                <SeeOurNetworkButton />
              </div>
            </div>
            <div className='py-4 md:py-0 flex flex-col gap-12'>
              {testimonialRowItems.map((item, index) => (
                <div key={index} className='flex gap-4 md:items-center'>
                  <CircleImage imageUrl={item.image} imageAlt={item.titleText} />
                  <div className='flex flex-col'>
                    <div className='text-sm text-brand-600'>{item.hightlightText}</div>
                    <div className='text-xl font-header font-semibold'>{item.titleText}</div>
                    <div className='text-sm italic'>{item.quoteText}</div>
                  </div>
                </div>
              ))}
              <div className='md:hidden mx-auto'>
                <SeeOurNetworkButton />
              </div>
            </div>
          </div>
        </div>
        {/* Social impact & Blog links */}
        <div className='bg-secondary-brand-25 px-4 py-12'>
          <div className='mx-auto w-fit flex flex-col items-center text-center gap-2'>
            <h1 className='text-4xl md:text-5xl font-bold text-gray-500'>More on Social Impact</h1>
            <h2 className='text-xl md:text-2xl font-header text-gray-500'>
              Read up on all things Social Impact on the MovingWorlds blog
            </h2>
            <Divider />
          </div>
          <div className='flex flex-col gap-10 items-center mb-4 max-w-[1440px] mx-auto'>
            <div className='grid grid-rows-3 md:grid-rows-1 md:grid-cols-3 mt-2 md:mt-8 gap-4 md:gap-8'>
              {blogCards.map((bc) => (
                <BlogCard
                  key={bc.title}
                  title={bc.title}
                  subtitle={bc.subtitle}
                  imageUrl={bc.imageUrl}
                  linkUrl={bc.linkUrl}
                />
              ))}
            </div>
            <Button
              variant='link-color'
              type='button'
              size='lg'
              href='https://blog.movingworlds.org/'
            >
              More articles
            </Button>
          </div>
        </div>
        <SocialImpactNews backgroundColor='gray' />
        {/* Corporate logos */}
        <div className='max-w-full mx-auto bg-white mt-12 overflow-clip'>
          <div className='max-w-[1390px] flex flex-col items-center'>
            <h2 className='font-header text-xl md:text-2xl text-gray-500 pt-8 pb-4'>As Seen In</h2>
            <div className='flex flex-wrap gap-6 justify-center'>
              <AsSeenIn />
            </div>
            <h2 className='font-header text-xl md:text-2xl text-gray-500 pt-16 pb-4'>Trusted By</h2>
            <div className='grid grid-cols-2 gap-2 md:grid-cols-6 md:gap-4'>
              <TrustedBy />
            </div>
          </div>
        </div>
      </div>
    </HeaderFooterLayout>
  );
};

export default HomePage;
